import React ,{ useEffect, useState} from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useNavigate, Link } from "react-router-dom";
import { stringify } from "querystring";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from 'react-helmet';

type SeoContent = {
  meta_title: string;
  meta_description?: string;
  // Add more fields as needed
};

const Home: React.FC = () => {


    const dispatch = useDispatch<any>();
    const [artefactList, setArtefactList] = useState('');
    const [soeContent, setSoeContent] = useState<SeoContent | null>(null);

    const artefacts = useSelector((state: any) => state.category.category_data || []);

    const data: any = [];


  useEffect(() => {
    if (artefacts?.data) {
      const rawCategories = artefacts.data;

      const mainCategories = rawCategories.filter((cat: any) => cat.parent_id === 0);
      const subCategories = rawCategories.filter((cat: any) => cat.parent_id !== 0);

      const result = mainCategories.map((mainCat: any) => {
        const children = subCategories
          .filter((sub: any) => sub.parent_id === mainCat.id)
          .map((sub: any) => ({
            name: sub.name,
            slug: sub.slug,
            icon: sub.icon || '',  // fallback to empty string
            description: sub.single_line,
            content: sub.content,
          }));

        return {
          title: `${mainCat.icon ? mainCat.icon + ' ' : ''}${mainCat.name}`,
          artefacts: children,
        };
      });

      setArtefactList(result);
      setSoeContent(artefacts?.seo_data);
    }
  }, [artefacts]);


  return (
    <>
    <Helmet>
      <title>{`${soeContent?.meta_title || 'Home'}`}</title>
      <meta name="title" content={soeContent?.meta_title || 'E-learning Platform'} />
      <meta name="description" content={soeContent?.meta_description || 'Learn with curated e-learning resources'} />
      <meta name="keywords" content="e-learning, tech-cards, cheat-sheets, tutorials" />
    </Helmet>

      <Header />

      {/* Hero Section */}
      <section
        className="text-white py-5 text-center"
        style={{ background: "linear-gradient(to right, #2563eb, #4f46e5)" }}
      >
        <div className="container">
          <h1 className="display-4 fw-bold">AI, ML, Data Science, and Software Development Made Easy</h1>
          <p className="lead mb-4">Explore Tech Cards, Cheat Sheets, Interview Kits, and Smart Courses at Uplatz</p>
          <Link className="btn btn-light btn-lg fw-semibold  me-2" target="_blank" to={`https://uplatz.com/online-courses`}>Explore Courses</Link>
          <Link to="#" className="btn btn-light btn-lg fw-semibold"
            onClick={(e) => {
              e.preventDefault();
              const section = document.getElementById("categories-list");
              if (section) {
                section.scrollIntoView({ behavior: "smooth" });
              }
            }} >
            Explore Categories
          </Link>
        </div>
      </section>

      {/* Explore Cards */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row g-4 text-center">
            {[
              { title: "Tech Cards", desc: "Quick learning blocks for every tech topic.", link: "/tech-cards" },
              { title: "Cheat Sheets", desc: "Concise reference guides for fast revision.", link: "/cheat-sheets" },
              { title: "Labs", desc: "Deep dive into AI, GenAI, ML, and more.", link: "/labs" },
              { title: "Tech Stack", desc: "Predefined combinations of tools/languages used in real projects.", link: "/tech-stacks" },
              { title: "Pocket Guides", desc: "1–2 hour read-friendly mini-books for quick mastery of a topic", link: "/pocket-guides" },
              { title: "Interview Kits", desc: "Curated resources for technical interviews (questions, answers, strategy).", link: "/interview-kits" },
            ].map((card) => (
              <div className="col-md-4" key={card.title}>
                <div className="card h-100 shadow-sm">
                  <div className="card-body">
                    <h5 className="card-title">{card.title}</h5>
                    <p className="card-text">{card.desc}</p>
                    <Link className="btn btn-outline-primary" to={card.link}>View More</Link>
                    {/* <a href={card.link} className="btn btn-outline-primary">View More</a> */}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-1">
        <div className="container">
          <h2 className="text-center fw-bold mb-5" id="categories-list">Browse eLearning Artifacts</h2>
          {Array.isArray(artefactList) && artefactList?.map((group : any, groupIndex: any) => (
            <div key={groupIndex} className="mb-5">
              <h5 className="mb-4">{group?.title}</h5>
              <ul className="list-unstyled">
                {group?.artefacts?.map((item: any, i: any) => (
                  <li key={i} className="mb-2">
                    <Link
                      to={item?.slug}
                      className="d-flex align-items-center p-3 rounded border shadow-sm text-decoration-none"
                      style={{
                        gap: "20px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item?.icon && (<span style={{ fontSize: "1.3rem" }}>{item.icon}</span>)}
                      <span className="fw-semibold text-dark">{item?.name}</span>
                      {(item?.content?.length > 0) && (<span className="badge bg-danger"  style={{borderRadius: '50%',minWidth: '25px',height: '25px',padding: '0',display: 'flex',alignItems: 'center',justifyContent: 'center', fontSize: '12px'  }}>{item?.content?.length}</span>)}
                      <span className="text-muted small" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                        – &nbsp; &nbsp; &nbsp; &nbsp; {item?.description}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* SaaS & Cloud Products */}
      <section className="py-5 bg-light">
        <div className="container text-center">
          <h2 className="mb-4 fw-bold">Uplatz SaaS & Cloud Products</h2>
          <p className="text-muted mb-5">
            Explore our cutting-edge SaaS & Cloud-based solutions to supercharge
            your business.
          </p>
          <div className="row g-4">
            {/* Product 1 */}
            <div className="col-md-4">
              <div className="card h-100 shadow-sm border-0">
                <img
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&h=400&q=80"
                  className="card-img-top"
                  alt="Cloud CRM"
                />
                <div className="card-body">
                  <h5 className="card-title">Uplatz Cloud CRM</h5>
                  <p className="card-text">
                    Manage your customers, leads, and deals with our powerful
                    cloud CRM platform.
                  </p>
                  {/* <a href="/products/cloud-crm" className="btn btn-primary">
                    Learn More
                  </a> */}
                </div>
              </div>
            </div>

            {/* Product 2 */}
            <div className="col-md-4">
              <div className="card h-100 shadow-sm border-0">
                <img
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&h=400&q=80"
                  className="card-img-top"
                  alt="SaaS Analytics"
                />
                <div className="card-body">
                  <h5 className="card-title">SaaS Analytics</h5>
                  <p className="card-text">
                    Get real-time analytics for your SaaS business with
                    AI-driven insights.
                  </p>
                  {/* <a href="/products/saas-analytics" className="btn btn-primary">
                    Learn More
                  </a> */}
                </div>
              </div>
            </div>

            {/* Product 3 */}
            <div className="col-md-4">
              <div className="card h-100 shadow-sm border-0">
                <img
                  src="https://images.unsplash.com/photo-1563986768609-322da13575f3?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&h=400&q=80"
                  className="card-img-top"
                  alt="Cloud Security Suite"
                />
                <div className="card-body">
                  <h5 className="card-title">Cloud Security Suite</h5>
                  <p className="card-text">
                    Protect your data and apps with enterprise-grade cloud
                    security solutions.
                  </p>
                  {/* <a href="/products/cloud-security" className="btn btn-primary">
                    Learn More
                  </a> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Products Section */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">Uplatz AI Products</h2>
            <p className="lead text-muted">
              Cutting-edge AI solutions transforming businesses
            </p>
          </div>

          <div className="row g-4">
            {/* AI Product 1 */}
            <div className="col-md-4">
              <div className="card h-100 ai-product-card border-0 shadow-sm">
                <img
                  src="https://images.unsplash.com/photo-1620712943543-bcc4688e7485?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&h=400&q=80"
                  className="card-img-top"
                  alt="AI Assistant"
                />
                <div className="card-body">
                  <div className="d-flex align-items-center mb-3">
                    <span className="badge bg-primary me-2">New</span>
                    <span className="badge bg-success">AI</span>
                  </div>
                  <h4 className="card-title">Uplatz AI Assistant</h4>
                  <p className="card-text">
                    Your intelligent virtual assistant powered by generative AI
                    to automate tasks and boost productivity.
                  </p>
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <i className="bi bi-check-circle-fill text-success me-2"></i>
                      Natural language processing
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle-fill text-success me-2"></i>
                      Task automation
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle-fill text-success me-2"></i>
                      24/7 availability
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* AI Product 2 */}
            <div className="col-md-4">
              <div className="card h-100 ai-product-card border-0 shadow-sm">
                <img
                  src="https://images.unsplash.com/photo-1573164713988-8665fc963095?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&h=400&q=80"
                  className="card-img-top"
                  alt="AI Analytics"
                />
                <div className="card-body">
                  <div className="d-flex align-items-center mb-3">
                    <span className="badge bg-warning me-2">Popular</span>
                    <span className="badge bg-success">AI</span>
                  </div>
                  <h4 className="card-title">Predictive Analytics AI</h4>
                  <p className="card-text">
                    Advanced machine learning models that predict trends and
                    behaviors with 95% accuracy.
                  </p>
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <i className="bi bi-check-circle-fill text-success me-2"></i>
                      Real-time predictions
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle-fill text-success me-2"></i>
                      Customizable models
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle-fill text-success me-2"></i>
                      Industry-specific solutions
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* AI Product 3 */}
            <div className="col-md-4">
              <div className="card h-100 ai-product-card border-0 shadow-sm">
                <img
                  src="https://images.unsplash.com/photo-1503437313881-503a91226402?q=80&w=600&h=400&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90oy1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                  className="card-img-top"
                  alt="AI Content Generator"
                />
                <div className="card-body">
                  <div className="d-flex align-items-center mb-3">
                    <span className="badge bg-danger me-2">Hot</span>
                    <span className="badge bg-success">AI</span>
                  </div>
                  <h4 className="card-title">Content Generator Pro</h4>
                  <p className="card-text">
                    Create high-quality content in seconds with our
                    state-of-the-art generative AI technology.
                  </p>
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <i className="bi bi-check-circle-fill text-success me-2"></i>
                      Blog articles
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle-fill text-success me-2"></i>
                      Marketing copy
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle-fill text-success me-2"></i>
                      Social media posts
                    </li>
                  </ul>
                </div>

              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">What Our Students & Clients Say</h2>
            <p className="lead text-muted">
              Trusted by thousands of learners and businesses worldwide
            </p>
          </div>

          <div className="row g-4">
            {/* Testimonial 1 */}
            <div className="col-md-4">
              <div className="card h-100 testimonial-card border-0 shadow-sm">
                <div className="card-body">
                  <div className="d-flex mb-3">
                    <img
                      src="https://randomuser.me/api/portraits/women/43.jpg"
                      className="rounded-circle me-3"
                      width="60"
                      height="60"
                      alt="Sarah Johnson"
                    />
                    <div>
                      <h5 className="mb-0">Sarah Johnson</h5>
                      <p className="text-muted mb-0">AI Engineer @TechCorp</p>
                      <div className="text-warning">
                        <i className="bi bi-star-fill"></i>
                        <i className="bi bi-star-fill"></i>
                        <i className="bi bi-star-fill"></i>
                        <i className="bi bi-star-fill"></i>
                        <i className="bi bi-star-fill"></i>
                      </div>
                    </div>
                  </div>
                  <p className="card-text">
                    "Uplatz's AI courses transformed my career. The hands-on
                    labs and real-world projects gave me the confidence to
                    implement AI solutions at my company."
                  </p>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="col-md-4">
              <div className="card h-100 testimonial-card border-0 shadow-sm">
                <div className="card-body">
                  <div className="d-flex mb-3">
                    <img
                      src="https://randomuser.me/api/portraits/men/32.jpg"
                      className="rounded-circle me-3"
                      width="60"
                      height="60"
                      alt="Michael Chen"
                    />
                    <div>
                      <h5 className="mb-0">Michael Chen</h5>
                      <p className="text-muted mb-0">CTO @StartUpX</p>
                      <div className="text-warning">
                        <i className="bi bi-star-fill"></i>
                        <i className="bi bi-star-fill"></i>
                        <i className="bi bi-star-fill"></i>
                        <i className="bi bi-star-fill"></i>
                        <i className="bi bi-star-half"></i>
                      </div>
                    </div>
                  </div>
                  <p className="card-text">
                    "We've been using Uplatz's AI products for 6 months now. The
                    predictive analytics tool alone has increased our revenue by
                    18% through better decision-making."
                  </p>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="col-md-4">
              <div className="card h-100 testimonial-card border-0 shadow-sm">
                <div className="card-body">
                  <div className="d-flex mb-3">
                    <img
                      src="https://randomuser.me/api/portraits/women/65.jpg"
                      className="rounded-circle me-3"
                      width="60"
                      height="60"
                      alt="Priya Patel"
                    />
                    <div>
                      <h5 className="mb-0">Priya Patel</h5>
                      <p className="text-muted mb-0">Data Science Student</p>
                      <div className="text-warning">
                        <i className="bi bi-star-fill"></i>
                        <i className="bi bi-star-fill"></i>
                        <i className="bi bi-star-fill"></i>
                        <i className="bi bi-star-fill"></i>
                        <i className="bi bi-star-fill"></i>
                      </div>
                    </div>
                  </div>
                  <p className="card-text">
                    "The tech cards and cheat sheets are game-changers! I can
                    quickly review complex ML concepts before interviews. Landed
                    my dream job thanks to Uplatz!"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Home;
